import {
	SERVER_URL,
	WEB_OAUTH_CLIENT,
	REST_OAUTH_SECRET,
	SCOPE,
	REALM_PATH,
	REALM_URL_PATH,
	JOURNEY_LOGIN,
	OAUTH_END_SESSION_URL
} from "../utils/constants.util"

// OAuth types
export interface TokenResponse {
	access_token: string
	token_type: string
	expires_in: number
	refresh_token?: string
	id_token?: string
	scope?: string
}

export interface UserInfo {
	sub?: string
	subname?: string
	username?: string
	name?: string
	email?: string
	preferred_username?: string
	given_name?: string
	family_name?: string
	[key: string]: unknown
}

export interface OAuthFlowResult {
	userInfo: UserInfo
	tokens: TokenResponse
}

/**
 * OAuth 2.0 Authorization Code Flow Service
 * Handles direct OAuth 2.0 authorization code flow using ONLY /authorize endpoint
 */
class OAuthService {
	private clientId: string
	private clientSecret: string | undefined
	private scope: string
	private realmPath: string
	private realmUrlPath: string
	private tree: string
	private baseUrl: string

	constructor() {
		this.clientId = WEB_OAUTH_CLIENT
		this.clientSecret = REST_OAUTH_SECRET
		this.scope = SCOPE
		this.realmPath = REALM_PATH
		this.realmUrlPath = REALM_URL_PATH
		this.tree = JOURNEY_LOGIN
		this.baseUrl = SERVER_URL
	}

	/**
	 * Generate authorization URL for OAuth 2.0 authorization code flow
	 * @param {string} state - Random state parameter for security
	 * @param {string} redirectUri - Redirect URI after authorization
	 * @returns {string} Authorization URL
	 */
	generateAuthorizationUrl(state: string, redirectUri: string): string {
		const params = new URLSearchParams({
			response_type: "code",
			client_id: this.clientId,
			scope: this.scope,
			state: state,
			redirect_uri: redirectUri,
			tree: this.tree
		})

		const authUrl = `${this.baseUrl}/oauth2/realms/root/${this.realmUrlPath}/authorize?${params.toString()}`

		// Security: Never log sensitive OAuth information (CWE-532)
		// Only log non-sensitive metadata in development mode
		if (process.env.NODE_ENV === "development") {
			console.log("OAuth authorization URL generated (sanitized):", {
				baseUrl: this.baseUrl,
				realm: this.realmPath,
				scope: this.scope,
				tree: this.tree
				// Never log: client_id, state, redirect_uri, or full URL with params
			})
		}

		return authUrl
	}

	/**
	 * Exchange authorization code for tokens
	 * @param {string} code - Authorization code from the redirect
	 * @param {string} redirectUri - Redirect URI (must match the one used in authorization)
	 * @returns {Promise<TokenResponse>} Token response
	 */
	async exchangeCodeForTokens(
		code: string,
		redirectUri: string
	): Promise<TokenResponse> {
		const tokenUrl = `${this.baseUrl}/oauth2/realms/root/${this.realmUrlPath}/access_token`
		const params = new URLSearchParams({
			scope: this.scope,
			grant_type: "authorization_code",
			client_id: this.clientId,
			...(this.clientSecret && { client_secret: this.clientSecret }),
			code: code,
			redirect_uri: redirectUri
		})

		const response = await fetch(tokenUrl, {
			method: "POST",
			headers: {
				"Content-Type": "application/x-www-form-urlencoded"
			},
			body: params.toString()
		})

		if (!response.ok) {
			const errorText = await response.text()
			throw new Error(`Token exchange failed: ${response.status} ${errorText}`)
		}

		return await response.json()
	}

	/**
	 * Silently obtain fresh tokens using an existing refresh token.
	 * Uses the OAuth 2.0 refresh_token grant — a first-party POST to AM where
	 * the refresh token itself is the credential. No SSO cookie, iframe, or
	 * redirect is involved, so it works regardless of third-party-cookie
	 * policy and is invisible to the user.
	 * Requires the login scope to include `offline_access` (and the OAuth
	 * client to be allowed to issue refresh tokens) so a refresh token exists.
	 * @param {string} refreshToken - Refresh token from a prior token response
	 * @returns {Promise<TokenResponse>} New token response (AM may rotate the refresh token)
	 */
	async refreshAccessToken(refreshToken: string): Promise<TokenResponse> {
		const tokenUrl = `${this.baseUrl}/oauth2/realms/root/${this.realmUrlPath}/access_token`
		const params = new URLSearchParams({
			grant_type: "refresh_token",
			client_id: this.clientId,
			...(this.clientSecret && { client_secret: this.clientSecret }),
			scope: this.scope,
			refresh_token: refreshToken
		})

		const response = await fetch(tokenUrl, {
			method: "POST",
			headers: {
				"Content-Type": "application/x-www-form-urlencoded"
			},
			body: params.toString()
		})

		if (!response.ok) {
			const errorText = await response.text()
			throw new Error(`Token refresh failed: ${response.status} ${errorText}`)
		}

		return await response.json()
	}

	/**
	 * Get user information using access token
	 * @param {string} accessToken - Access token
	 * @returns {Promise<UserInfo>} User information
	 */
	async getUserInfo(accessToken: string): Promise<UserInfo> {
		const userInfoUrl = `${this.baseUrl}/oauth2/realms/root/${this.realmUrlPath}/userinfo`

		const response = await fetch(userInfoUrl, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${accessToken}`,
				"Content-Type": "application/json"
			}
		})

		if (!response.ok) {
			throw new Error(`User info request failed: ${response.status}`)
		}

		return await response.json()
	}

	/**
	 * Store tokens in the SDK's token storage
	 * @param {TokenResponse} tokenResponse - Token response from exchange
	 */
	async storeTokens(_tokenResponse: TokenResponse): Promise<void> {
		//await TokenManager.setTokens(tokenResponse);
	}

	/**
	 * Complete OAuth flow with authorization code
	 * @param {string} code - Authorization code
	 * @param {string} state - State parameter for verification
	 * @param {string} redirectUri - Redirect URI
	 * @returns {Promise<OAuthFlowResult>} User information and tokens
	 */
	async completeOAuthFlow(
		code: string,
		_state: string,
		redirectUri: string
	): Promise<OAuthFlowResult> {
		try {
			const tokenResponse = await this.exchangeCodeForTokens(code, redirectUri)
			//await this.storeTokens(tokenResponse);
			const userInfo = await this.getUserInfo(tokenResponse.access_token)
			return { userInfo, tokens: tokenResponse }
		} catch (error) {
			console.error("OAuth flow error:", error)
			throw error
		}
	}

	/**
	 * Logout from ForgeRock OAuth session
	 * @param {string} idToken - ID token for logout
	 * @param {string} postLogoutRedirectUri - Redirect URI after logout
	 * @returns {Promise<void>}
	 */
	async logout(idToken: string, postLogoutRedirectUri?: string): Promise<void> {
		try {
			const params = new URLSearchParams({
				id_token_hint: idToken,
				...(postLogoutRedirectUri && {
					post_logout_redirect_uri: postLogoutRedirectUri
				})
			})

			const logoutUrl = `${OAUTH_END_SESSION_URL}?${params.toString()}`

			// Security: Never log sensitive OAuth information (CWE-532)
			// Never log logout URL containing id_token_hint
			if (process.env.NODE_ENV === "development") {
				console.log("ForgeRock logout initiated (sanitized):", {
					endpoint: OAUTH_END_SESSION_URL
					// Never log: id_token_hint, post_logout_redirect_uri, or full URL
				})
			}

			// Redirect to the logout endpoint
			window.location.href = logoutUrl
		} catch (error) {
			console.error("ForgeRock logout error:", error)
			throw error
		}
	}

	/**
	 * Get stored tokens from secure storage
	 * @returns {Promise<{accessToken?: string, idToken?: string}>}
	 */
	async getStoredTokens(): Promise<{ accessToken?: string; idToken?: string }> {
		try {
			// Import the AuthService to access token functions
			const { AuthService } = await import("./authService")
			const accessToken = AuthService.getAuthToken()
			const idToken = AuthService.getIdToken()
			return { accessToken, idToken }
		} catch (error) {
			console.error("Error getting stored tokens:", error)
			return {}
		}
	}
}

export default new OAuthService()
